import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import Calendar from "react-calendar";
import {
  parseISO,
  getDay,
  format,
  add,
  isAfter,
  getHours,
  getMinutes,
  isWithinInterval,
} from "date-fns";
import { getSpecialistByNombres } from "../../actions/especialistas";
import { updateCheckout } from "../../actions/checkout";
import Spinner from "../layout/Spinner";
import Progress from "./Progress";
import "./Calendar.css";

const TerapeutaHorario = ({
  match,
  updateCheckout,
  specialists,
  user,
  getSpecialistByNombres,
}) => {
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [selectedHour, setSelectedHour] = useState("");

  useEffect(() => {
    getSpecialistByNombres((match.params.nombres || "").replace("-", " "));
  }, [getSpecialistByNombres, match.params.nombres]);

  const info = specialists.specialist;
  const completed = () => {
    if (selectedHour) {
      updateCheckout({
        cliente: user._id,
        newSession: true,
        especialista: info._id,
        tarifa: info.tarifas,
        horaPactada: add(selectedDay, {
          hours: selectedHour.split(":")[0],
          minutes: selectedHour.split(":")[1],
        }),
        reagendo: false,
      });
    }
  };

  let availableHorarios = [];
  if (info && info.horarios) {
    const sesionesOcupadas = info.sessions.map((sessionInfo) =>
      parseISO(sessionInfo.horaPactada)
    );

    Object.values(info.horarios).forEach((val) => {
      let horario = parseISO(val);
      let fechaDisponible = add(selectedDay, {
        hours: getHours(horario),
        minutes: getMinutes(horario),
      });
      if (
        getDay(horario) === getDay(selectedDay) && // Same day
        isAfter(selectedDay, add(new Date(), { days: 1 })) && // After one day
        sesionesOcupadas.every(
          (sesion) =>
            !isWithinInterval(fechaDisponible, {
              start: sesion,
              end: add(sesion, { minutes: 50 }),
            })
        ) // Is not already taken
      ) {
        availableHorarios.push(format(fechaDisponible, "HH:mm"));
      }
    });
  }

  return specialists.loading || !info ? (
    <Spinner />
  ) : (
    <section
      className="container px-8 pt-8 flex-grow flex flex-col mx-auto font-sans"
      style={{ height: "max-content" }}
    >
      <span className="font-bold mb-2">2. Horario</span>
      <Progress selected={1} />

      {/* Therapist Info Card - Same style as Terapeutas page */}
      <div className="bg-white p-6 rounded-lg shadow-lg mt-4 mb-4">
        <div className="flex items-center">
          <img
            className="h-20 w-20 object-cover rounded-full"
            src={info.avatar}
            alt="Foto de Perfil"
          />
          <div className="ml-4 tracking-wide w-full">
            <div className="flex items-baseline justify-between">
              <h4 className="text-xl font-bold">{info.nombres}</h4>
              <div className="flex flex-col items-center text-higia-cyan">
                <svg
                  className="h5 w-5 fill-current wavesShadow"
                  viewBox="0 0 32 29.6"
                >
                  <path
                    d="M23.6,0c-3.4,0-6.3,2.7-7.6,5.6C14.7,2.7,11.8,0,8.4,0C3.8,0,0,3.8,0,8.4c0,9.4,9.5,11.9,16,21.2
    c6.1-9.3,16-12.1,16-21.2C32,3.8,28.2,0,23.6,0z"
                  />
                </svg>
                <div className="text-gray-800">{info.numSes}</div>
              </div>
            </div>
            {info.longDesc && (
              <p className="text-sm whitespace-pre-wrap">{info.longDesc}</p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="text-sm">
            {info.tarifas &&
              Object.keys(info.tarifas)
                .sort()
                .map((serv) => (
                  <div key={serv}>
                    {serv + ": "}
                    {info.tarifas[serv].split(" ").length > 1 ? (
                      <>
                        <span className="line-through">
                          {"S/" + info.tarifas[serv].split(" ")[0]}
                        </span>
                        <span className="text-red-500 font-bold">
                          {" S/" + info.tarifas[serv].split(" ")[1]}
                        </span>
                      </>
                    ) : (
                      <span>{"  S/" + info.tarifas[serv]}</span>
                    )}
                  </div>
                ))}
          </div>
        </div>
      </div>

      <div className="font-bold text-lg mt-2 mb-6">Escoge la fecha</div>
      <Calendar
        onChange={(value) => {
          setSelectedDay(value);
          setSelectedHour("");
        }}
        value={selectedDay}
        locale="es-ES"
      />
      <span className="font-bold mt-8 mb-4">Horarios Disponibles</span>
      <div className="flex flex-wrap mb-2">
        {availableHorarios.length > 0 ? (
          availableHorarios.sort().map((hora) => (
            <div
              className={`flex align-center border-solid cursor-pointer border-2 rounded px-3 mr-3 mb-2 ${
                selectedHour === hora
                  ? "border-higia-bermuda"
                  : "border-higia-gray-2"
              }`}
              key={hora}
              style={{ alignItems: "center" }}
              onClick={() => {
                setSelectedHour(hora);
              }}
            >
              <div
                className={`w-3 h-3 rounded-full inline border border-solid border-black mr-2 ${
                  selectedHour === hora ? "bg-higia-bermuda" : ""
                }`}
              ></div>
              <div className="">{hora}</div>
            </div>
          ))
        ) : (
          <div>No hay horarios disponibles este día</div>
        )}
      </div>
      {/* Summary card when hour is selected */}
      {selectedHour && (
        <div className="bg-higia-cyan bg-opacity-10 p-4 rounded-lg mt-6 border-2 border-higia-cyan">
          <h3 className="font-bold text-higia-gray-3 mb-2">Resumen de tu cita</h3>
          <div className="text-sm text-higia-gray-3">
            <div><span className="font-semibold">Terapeuta:</span> {info.nombres}</div>
            <div><span className="font-semibold">Fecha:</span> {format(selectedDay, 'dd/MM/yyyy')}</div>
            <div><span className="font-semibold">Hora:</span> {selectedHour}</div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mt-6 mb-10">
        <Link
          className="w-20 text-white p-2 rounded flex justify-center bg-higia-bermuda hover:bg-opacity-90 transition"
          to={`${match.url.replace("/horarios", "")}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 8 12"
            className="fill-current "
            width="12"
            height="18"
            style={{ transform: "scale(-1, 1)" }}
          >
            <path
              d="M2.173 1l4.584 4.725-4.615 4.615-1.103-1.103 3.512-3.512L1 2.173
          2.173 1z"
            />
          </svg>
        </Link>
        <Link
          className={`flex-1 ml-4 text-white py-3 px-6 rounded-lg flex items-center justify-center font-semibold text-lg transition ${
            selectedHour
              ? "bg-higia-bermuda hover:bg-opacity-90 shadow-lg"
              : "bg-gray-400 cursor-not-allowed"
          }`}
          to={`${selectedHour ? `/casiacabamos` : "#"}`}
          onClick={() => selectedHour && completed()}
        >
          <span>Reservar Cita</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 8 12"
            className="fill-current ml-2"
            width="12"
            height="18"
          >
            <path
              d="M2.173 1l4.584 4.725-4.615 4.615-1.103-1.103 3.512-3.512L1 2.173
          2.173 1z"
            />
          </svg>
        </Link>
      </div>
    </section>
  );
};

const mapStateToProps = (state) => ({
  specialists: state.specialists,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  getSpecialistByNombres,
  updateCheckout,
})(TerapeutaHorario);
